'use client';

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import * as React from 'react';

import { FarIcons } from '@playbooks/icons';
import { Button } from '@playbooks/ui/components/button';
import { backdropVariants, panelElevationVariants } from '@playbooks/ui/components/component-recipes';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';
import { cn } from '@playbooks/ui/lib/utils';

import { type VariantProps, cva } from 'class-variance-authority';

const dialogBodyVariants = cva('min-h-0 overflow-y-auto', {
	variants: {
		density: {
			compact: 'p-3',
			default: 'p-4',
			comfortable: 'p-5',
		},
	},
	defaultVariants: { density: 'default' },
});

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
	return <DialogPrimitive.Root data-slot='dialog' {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
	return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
	return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
	return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
	// Theme
	const rootClassName = cn(
		backdropVariants({ strength: 'strong', motion: 'fast' }),
		'isolate backdrop-blur-[2px] data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
		className,
	);

	// Render
	return <DialogPrimitive.Backdrop data-slot='dialog-overlay' className={rootClassName} {...props} />;
}

function DialogContent({
	className,
	children,
	showCloseButton = true,
	variant = 'default',
	size,
	...props
}: DialogPrimitive.Popup.Props & {
	showCloseButton?: boolean;
	variant?: 'default' | 'form';
	size?: 'sm' | 'lg' | 'xl';
}) {
	const resolvedSize = size ?? 'sm';

	// Theme
	const rootClassName = cn(
		panelElevationVariants({ elevation: 'overlay', surface: 'raised' }),
		'group/dialog-content fixed top-1/2 left-1/2 z-overlay grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-panel p-4 text-sm duration-fast outline-none data-[size=lg]:sm:max-w-lg data-[size=sm]:sm:max-w-sm data-[size=xl]:sm:max-w-4xl data-[variant=form]:max-h-[calc(100vh-2rem)] data-[variant=form]:gap-0 data-[variant=form]:overflow-hidden data-[variant=form]:p-0 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[variant=form]:[&>form]:flex data-[variant=form]:[&>form]:min-h-0 data-[variant=form]:[&>form]:flex-col data-[variant=form]:[&>form>div:not([data-slot])]:min-h-0 data-[variant=form]:[&>form>div:not([data-slot])]:overflow-y-auto data-[variant=form]:[&>form>div:not([data-slot])]:p-4 data-[variant=form]:[&>div:not([data-slot])]:min-h-0 data-[variant=form]:[&>div:not([data-slot])]:overflow-y-auto data-[variant=form]:[&>div:not([data-slot])]:p-4',
		className,
	);

	// Render
	return (
		<DialogPortal>
			<DialogOverlay />
			<DialogPrimitive.Popup
				data-slot='dialog-content'
				data-variant={variant}
				data-size={resolvedSize}
				className={rootClassName}
				{...props}>
				{children}
				{showCloseButton && (
					<DialogPrimitive.Close
						data-slot='dialog-close'
						render={<Button variant='ghost' className='absolute top-2 right-2' size='icon-sm' />}>
						<StructuralIcon icon={FarIcons.farXmark} />
						<span className='sr-only'>Close</span>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Popup>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn(
		'flex flex-col gap-1.5 group-data-[variant=form]/dialog-content:shrink-0 group-data-[variant=form]/dialog-content:border-b group-data-[variant=form]/dialog-content:border-border group-data-[variant=form]/dialog-content:px-4 group-data-[variant=form]/dialog-content:py-3.5',
		className,
	);

	// Render
	return <div data-slot='dialog-header' className={rootClassName} {...props} />;
}

function DialogFooter({
	className,
	showCloseButton = false,
	children,
	...props
}: React.ComponentProps<'div'> & {
	showCloseButton?: boolean;
}) {
	// Theme
	const rootClassName = cn(
		'flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end',
		'group-data-[variant=form]/dialog-content:m-0 group-data-[variant=form]/dialog-content:shrink-0 group-data-[variant=form]/dialog-content:border-t group-data-[variant=form]/dialog-content:border-border group-data-[variant=form]/dialog-content:bg-surface-raised group-data-[variant=form]/dialog-content:px-4 group-data-[variant=form]/dialog-content:py-3',
		className,
	);

	// Render
	return (
		<div data-slot='dialog-footer' className={rootClassName} {...props}>
			{children}
			{showCloseButton && <DialogPrimitive.Close render={<Button variant='outline' />}>Close</DialogPrimitive.Close>}
		</div>
	);
}

function DialogBody({
	className,
	density = 'default',
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof dialogBodyVariants>) {
	// Theme
	const rootClassName = cn(dialogBodyVariants({ density }), className);

	// Render
	return <div data-slot='dialog-body' data-density={density} className={rootClassName} {...props} />;
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
	// Theme
	const rootClassName = cn('text-base leading-tight font-semibold tracking-tight', className);

	// Render
	return <DialogPrimitive.Title data-slot='dialog-title' className={rootClassName} {...props} />;
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
	// Theme
	const rootClassName = cn(
		'text-sm text-foreground-body *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
		className,
	);

	// Render
	return <DialogPrimitive.Description data-slot='dialog-description' className={rootClassName} {...props} />;
}

export {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
